import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';

/** Roles that can action the pending_my_approval timesheet filter. */
export const TIMESHEET_APPROVER_ROLES = [
    'admin',
    'director',
    'finance',
    'hr_manager',
    'manager',
    'project_manager',
    'team_leader',
];

const REFRESH_MS = 60_000;

/**
 * Fetches how many timesheets await the current user's approval action.
 *
 * @returns {{ count: number, loading: boolean, refresh: () => Promise<void> }}
 */
export function usePendingTimesheetApprovalCount() {
    const { user, token } = useAuth();
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const canApprove = Boolean(
        user
        && (
            TIMESHEET_APPROVER_ROLES.includes(user.role)
            || user.can_admin_approve
        )
    );

    const refresh = useCallback(async () => {
        if (!token || !canApprove) {
            setCount(0);
            return;
        }

        setLoading(true);
        try {
            const response = await apiClient.get('/timesheets/', {
                params: {
                    pending_my_approval: 'true',
                    page_size: 1,
                },
            });
            const payload = response.data;
            const nextCount = typeof payload?.count === 'number'
                ? payload.count
                : Array.isArray(payload?.results)
                    ? payload.results.length
                    : 0;
            setCount(nextCount);
        } catch {
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [token, canApprove]);

    useEffect(() => {
        refresh();
        const intervalId = window.setInterval(refresh, REFRESH_MS);
        return () => window.clearInterval(intervalId);
    }, [refresh]);

    return { count, loading, refresh, canApprove };
}
